import {authBrokerEndpoint} from '../config/authBroker.ts';

/**
 * Exchange the portal's freshly-minted Cognito tokens for a single-use
 * authorization code (FNP-871).
 *
 * The portal has already authenticated the user; this hands those tokens to
 * the broker, which stores them against an opaque code bound to the calling
 * app's client_id, redirect_uri and PKCE challenge. The app then redeems the
 * code over POST.
 *
 * ⚠️ NEVER LOG the arguments or the return value of this function. The inputs
 * are raw Cognito tokens and the output is a code that redeems to them. FNP-864
 * and FNP-868 were both this exact mistake.
 */

/**
 * Thrown when a mint for this request id is already in flight or done.
 *
 * NOT a failure. The winning mount is completing the sign-in and will navigate
 * away; a losing mount that treats this as an error would replace a successful
 * sign-in with an error screen — exactly the bug this whole guard exists to
 * prevent. Callers must swallow it and render the loader instead.
 */
export class MintAlreadyInFlightError extends Error {
    constructor() {
        super('A mint for this request is already in flight.');
        this.name = 'MintAlreadyInFlightError';
    }
}

export interface MintCodeParams {
    // Which component initiated this. Logged by the broker so a duplicate mint
    // can be attributed to a caller instead of guessed at — two components
    // racing over one single-use parked request is invisible otherwise.
    readonly caller: string;
    // The id the broker handed us as `state` on the way in. The broker uses it
    // to recover the ORIGINATING APP's redirect_uri, client_id, PKCE challenge
    // and state — none of which the portal knows, or needs to know.
    readonly requestId: string;
    readonly idToken: string;
    readonly accessToken: string;
    // Required (FNP-1358). Without it the app's session cannot refresh and ends
    // with the access token, at 60 minutes. Take it from loadFreshSession.
    readonly refreshToken: string;
}

/**
 * One mint per request id, enforced OUTSIDE React.
 *
 * ⚠️ THIS IS THE FIX THAT ACTUALLY WORKS. Three earlier attempts guarded with a
 * `useRef` inside a component and all three failed, because the component
 * REMOUNTS while `useAuth()`'s user/idToken/accessToken settle at different
 * times — and a remount gives a fresh `useRef(false)`, so the guard resets.
 * Attribution logging proved it: two `authorize` POSTs ~1s apart, both tagged
 * `caller: "AuthorizePage"`, i.e. one component firing twice, not two racing.
 *
 * The parked request is single-use, so the second POST always fails with
 * `unknown_request` and — worse — the losing component then sets an error state
 * that discards a sign-in which had ALREADY succeeded.
 *
 * sessionStorage survives remounts, is per-tab, and is keyed by request id so a
 * genuinely new sign-in is never blocked by an old one.
 */
const claimKey = (requestId: string) => `flexinet.mint.${requestId}`;
const resultKey = (requestId: string) => `flexinet.mint.result.${requestId}`;

const claimMint = (requestId: string): boolean => {
    try {
        if (sessionStorage.getItem(claimKey(requestId)) !== null) {
            return false;
        }
        sessionStorage.setItem(claimKey(requestId), '1');
        return true;
    } catch {
        // Private browsing / storage disabled. Fail OPEN: a duplicate mint is
        // recoverable, a sign-in blocked outright is not.
        return true;
    }
};

/**
 * The winner RECORDS its result; a loser can then use it.
 *
 * Without this, a remount that loses the claim has no way to finish the
 * sign-in — and if the winning mount was unmounted before it could navigate,
 * nobody navigates at all. Recording the destination means whichever mount is
 * alive can complete the redirect, which makes the flow robust to remounting
 * instead of merely tolerant of it.
 */
const recordMintResult = (requestId: string, redirectTo: string): void => {
    try {
        sessionStorage.setItem(resultKey(requestId), redirectTo);
    } catch {
        // Non-fatal: the winner still navigates directly.
    }
};

export const getMintResult = (requestId: string): string | null => {
    try {
        return sessionStorage.getItem(resultKey(requestId));
    } catch {
        return null;
    }
};

export const mintAuthorizationCode = async (
    params: MintCodeParams
): Promise<string> => {
    // Claimed here rather than at a call site so EVERY caller is covered by the
    // same rule — the previous design left each component to guard itself, and
    // each one guarded only its own instance.
    if (!claimMint(params.requestId)) {
        // Someone else already minted for this request. If they recorded the
        // destination, USE IT — that completes the sign-in from whichever mount
        // happens to be alive. Otherwise the winner is still in flight and will
        // navigate itself.
        const existing = getMintResult(params.requestId);
        if (existing) {
            return existing;
        }
        throw new MintAlreadyInFlightError();
    }

    const response = await fetch(`${authBrokerEndpoint}/oauth2/authorize`, {
        method: 'POST',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify({
            request_id: params.requestId,
            caller: params.caller,
            id_token: params.idToken,
            access_token: params.accessToken,
            refresh_token: params.refreshToken,
        }),
    });

    if (!response.ok) {
        // Surface the broker's error CODE only. The response body is safe here
        // (it carries no secret), but the request body is not — so never widen
        // this to log what was sent.
        const detail = await response.json().catch(() => ({}));
        throw new Error(
            `Auth broker rejected the code request: ${detail?.error ?? response.status}`
        );
    }

    const {redirect_to: redirectTo} = await response.json();

    // The broker returns the fully-built destination: the app's own
    // redirect_uri with `?code=` and the app's original `state`. The portal
    // navigates to it verbatim and never reconstructs it — the values were
    // parked broker-side precisely so they could not be altered in transit.
    if (typeof redirectTo !== 'string' || redirectTo === '') {
        throw new Error('Auth broker returned no redirect target.');
    }

    // Record BEFORE returning, so a mount that lost the claim can still finish
    // the sign-in if this one is unmounted before it navigates.
    recordMintResult(params.requestId, redirectTo);

    return redirectTo;
};
